/**
 * Create and edit an entry. The prototype could only add and delete; editing an
 * existing entry was the first ✅ of features.md §5, and it is the same form —
 * selecting a bar in the chart loads it here.
 */

import type { Category, CategoryId, Item } from '../../model/types';
import { field, h, icon, numberValue, option } from './dom';

export interface EntryFormHandlers {
  onSubmit(item: Item, index: number | null): void;
  onDelete(index: number): void;
  onLaneNudge(index: number, delta: number): void;
  onLaneReset(index: number): void;
  onCancel(): void;
}

export interface EntrySelection {
  index: number;
  item: Item;
  /** The lane the entry occupies right now, pinned or computed. */
  lane: number;
}

export function createEntryForm(handlers: EntryFormHandlers) {
  const type = h('select', { class: 'f-type' });
  type.append(option('period', 'Période'), option('event', 'Événement ponctuel'));

  const label = h('input', { type: 'text', placeholder: 'ex: Louis 14 (règne)' });
  const category = h('select', {});

  const start = h('input', { type: 'number' });
  const end = h('input', { type: 'number' });

  const year = h('input', { type: 'number' });
  const spanFrom = h('input', { type: 'number', placeholder: 'de' });
  const spanTo = h('input', { type: 'number', placeholder: 'à' });

  const periodFields = h('div', { class: 'field-group' }, field('Début', start), field('Fin', end));

  const eventFields = h(
    'div',
    { class: 'field-group' },
    field('Année', year),
    field('Date incertaine entre', spanFrom),
    field('et', spanTo),
  );

  const submit = h('button', { text: 'Ajouter', dataset: { action: 'entry-submit' } });
  const remove = h('button', { text: 'Supprimer', class: 'danger', dataset: { action: 'entry-delete' } });

  // The cross in the header is the only way out besides saving: a separate
  // "Annuler" button said the same thing twice.
  const close = h('button', {
    class: 'dialog-close',
    title: 'Fermer',
    dataset: { action: 'entry-close' },
  });
  close.setAttribute('aria-label', 'Fermer');
  close.append(icon('close'));

  const laneUp = h('button', { text: '↑', title: 'Monter d’une ligne', dataset: { action: 'lane-up' } });
  const laneDown = h('button', { text: '↓', title: 'Descendre d’une ligne', dataset: { action: 'lane-down' } });
  const laneAuto = h('button', { text: 'auto', title: 'Rendre la ligne automatique', dataset: { action: 'lane-auto' } });
  const laneControls = h('div', { class: 'lane-controls' }, 'Ligne', laneUp, laneDown, laneAuto);

  const error = h('div', { class: 'error', hidden: true });
  const title = h('span', { text: 'Ajouter une entrée' });
  const heading = h('h2', {}, title, close);

  // A dialog rather than a panel at the top of the page: an entry on the fourth
  // band is edited without scrolling away from it, and back again.
  const el = h(
    'dialog',
    { class: 'entry-dialog', dataset: { panel: 'entry' } },
    heading,
    h('div', { class: 'form-row' }, field('Type', type), field('Libellé', label), field('Catégorie', category)),
    periodFields,
    eventFields,
    h('div', { class: 'form-row' }, submit, remove, laneControls),
    error,
  );

  let selection: EntrySelection | null = null;

  const showFieldsFor = (kind: string) => {
    periodFields.hidden = kind !== 'period';
    eventFields.hidden = kind !== 'event';
  };
  type.addEventListener('change', () => showFieldsFor(type.value));

  const fail = (message: string): null => {
    error.hidden = false;
    error.textContent = message;
    return null;
  };

  const read = (): Item | null => {
    error.hidden = true;

    const text = label.value.trim();
    if (text === '') return fail('Le libellé est obligatoire.');
    if (category.value === '') return fail('Choisis une catégorie (crée-en une si la liste est vide).');

    const lane = selection?.item.lane;

    if (type.value === 'period') {
      const from = numberValue(start);
      const to = numberValue(end);
      if (from === null || to === null) return fail('Début et fin sont obligatoires pour une période.');
      if (to < from) return fail(`La fin (${to}) précède le début (${from}).`);

      const item: Item = { type: 'period', cat: category.value, label: text, start: from, end: to };
      if (lane !== undefined) item.lane = lane;
      return item;
    }

    const at = numberValue(year);
    if (at === null) return fail("L'année est obligatoire pour un événement.");

    const from = numberValue(spanFrom);
    const to = numberValue(spanTo);
    if ((from === null) !== (to === null)) return fail('Une fourchette demande ses deux bornes.');

    const item: Item = { type: 'event', cat: category.value, label: text, year: at };
    if (from !== null && to !== null) {
      if (to < from) return fail(`Fourchette inversée (${from} → ${to}).`);
      if (at < from || at > to) return fail(`L'année ${at} est hors de sa fourchette (${from} → ${to}).`);
      item.span = [from, to];
    }
    if (lane !== undefined) item.lane = lane;
    return item;
  };

  const dismiss = () => {
    if (el.open) el.close();
  };

  submit.addEventListener('click', () => {
    const item = read();
    if (!item) return;
    handlers.onSubmit(item, selection?.index ?? null);
    dismiss();
  });
  remove.addEventListener('click', () => {
    if (!selection) return;
    handlers.onDelete(selection.index);
    dismiss();
  });
  close.addEventListener('click', () => {
    dismiss();
    handlers.onCancel();
  });
  // Escape closes the dialog on its own; the selection has to follow.
  el.addEventListener('cancel', () => handlers.onCancel());

  // A click on the backdrop targets the dialog itself, since the backdrop is
  // part of its box — so compare against the sheet's own rectangle.
  el.addEventListener('click', (event) => {
    if (event.target !== el) return;
    const box = el.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (inside) return;

    dismiss();
    handlers.onCancel();
  });
  laneUp.addEventListener('click', () => {
    if (selection) handlers.onLaneNudge(selection.index, -1);
  });
  laneDown.addEventListener('click', () => {
    if (selection) handlers.onLaneNudge(selection.index, 1);
  });
  laneAuto.addEventListener('click', () => {
    if (selection) handlers.onLaneReset(selection.index);
  });

  const clearFields = () => {
    label.value = '';
    for (const input of [start, end, year, spanFrom, spanTo]) input.value = '';
  };

  return {
    el,

    open(): void {
      if (!el.open) el.showModal();
    },

    close: dismiss,

    setCategories(categories: Record<CategoryId, Category>): void {
      const previous = category.value;
      category.replaceChildren();
      for (const [id, value] of Object.entries(categories)) category.append(option(id, value.name));
      category.value = previous in categories ? previous : (Object.keys(categories)[0] ?? '');
    },

    select(next: EntrySelection | null): void {
      selection = next;
      error.hidden = true;

      title.textContent = next ? `Modifier « ${next.item.label} »` : 'Ajouter une entrée';
      submit.textContent = next ? 'Enregistrer' : 'Ajouter';
      remove.hidden = next === null;
      laneControls.hidden = next === null;
      laneAuto.disabled = next?.item.lane === undefined;

      if (!next) {
        clearFields();
        showFieldsFor(type.value);
        return;
      }

      const item = next.item;
      type.value = item.type;
      label.value = item.label;
      category.value = item.cat;
      showFieldsFor(item.type);

      if (item.type === 'period') {
        start.value = String(item.start);
        end.value = String(item.end);
      } else {
        year.value = String(item.year);
        spanFrom.value = item.span ? String(item.span[0]) : '';
        spanTo.value = item.span ? String(item.span[1]) : '';
      }
    },
  };
}
